export interface OfficeHourDay {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

const WEEK_DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

function dayKey(day: string | null | undefined): string {
  return (day || '').trim().toUpperCase().slice(0, 3);
}

/**
 * The office_hours feed omits days the office doesn't open (e.g. Sunday).
 * Append those days with no times so every hours list renders them as "Closed".
 * An empty feed stays empty so components keep their no-hours state.
 */
export function withClosedDays<T extends OfficeHourDay>(entries: T[] | null | undefined): T[] {
  if (!entries || entries.length === 0) return [];
  const present = new Set(entries.map((e) => dayKey(e.day)));
  const missing = WEEK_DAYS.filter((d) => !present.has(d)).map(
    (day) => ({ day, open_time: null, close_time: null }) as T,
  );
  return [...entries, ...missing];
}

export interface HoursRow {
  label: string;
  hours: string;
}

/**
 * Collapse consecutive days that share the same hours into one row
 * ("MON–FRI"). Rows must already be in week order.
 */
export function groupLikeHours(rows: HoursRow[], separator = '–'): HoursRow[] {
  const groups: Array<HoursRow & { first: string; last: string }> = [];
  for (const row of rows) {
    const prev = groups[groups.length - 1];
    if (prev && prev.hours === row.hours) {
      prev.last = row.label;
    } else {
      groups.push({ ...row, first: row.label, last: row.label });
    }
  }
  return groups.map(({ first, last, hours }) => ({
    label: first === last ? first : `${first}${separator}${last}`,
    hours,
  }));
}
